import { ApiResponseError, type ApiClient } from "@shell/auth/apiClient";
import { exportListResponseSchema } from "../common/schemas";
import {
    assetListResponseSchema,
    assetMutationResponseSchema,
    assetViewSchema,
    bulkImportAssetsResponseSchema,
    type AssetUpsertPayload,
    type AssetView,
    type BulkImportAssetsPayload,
    type BulkImportAssetsResult,
} from "./types";

interface SearchParams {
    readonly q?: string;
    readonly sageOfficeId?: string;
    readonly type?: string;
    readonly assetTag?: string;
    readonly serialNumber?: string;
    readonly includeDeleted?: boolean;
}

export async function searchAssets(
    client: ApiClient,
    params: SearchParams,
): Promise<readonly AssetView[]> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.sageOfficeId !== undefined && params.sageOfficeId.length > 0)
        search.set("sageOfficeId", params.sageOfficeId);
    if (params.type !== undefined && params.type.length > 0) search.set("type", params.type);
    if (params.assetTag !== undefined && params.assetTag.length > 0)
        search.set("assetTag", params.assetTag);
    if (params.serialNumber !== undefined && params.serialNumber.length > 0)
        search.set("serialNumber", params.serialNumber);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");

    const path = search.size > 0 ? `/api/assets?${search.toString()}` : "/api/assets";
    const raw = await client.getJson<unknown>(path);
    return assetListResponseSchema.parse(raw).results;
}

export async function exportAssets(
    client: ApiClient,
    params: SearchParams,
): Promise<{ readonly results: readonly AssetView[]; readonly truncated: boolean }> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.sageOfficeId !== undefined && params.sageOfficeId.length > 0)
        search.set("sageOfficeId", params.sageOfficeId);
    if (params.type !== undefined && params.type.length > 0) search.set("type", params.type);
    if (params.assetTag !== undefined && params.assetTag.length > 0)
        search.set("assetTag", params.assetTag);
    if (params.serialNumber !== undefined && params.serialNumber.length > 0)
        search.set("serialNumber", params.serialNumber);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");

    const path =
        search.size > 0 ? `/api/exports/assets?${search.toString()}` : "/api/exports/assets";
    const raw = await client.getJson<unknown>(path);
    return exportListResponseSchema(assetViewSchema).parse(raw);
}

export async function getAsset(
    client: ApiClient,
    id: string,
    includeDeleted: boolean,
): Promise<AssetView> {
    const search = new URLSearchParams();
    if (includeDeleted) search.set("includeDeleted", "true");
    const path =
        search.size > 0
            ? `/api/assets/${encodeURIComponent(id)}?${search.toString()}`
            : `/api/assets/${encodeURIComponent(id)}`;
    const raw = await client.getJson<unknown>(path);
    const parsed = assetMutationResponseSchema.parse(raw);
    return parsed.asset;
}

/**
 * Resolves a single asset id to its view. Returns null when the id is
 * empty or the API has no record (the SPA treats this as a benign
 * "unknown" state and falls back to displaying the raw id wherever the
 * view would have appeared). `includeDeleted=true` so assignment detail
 * panels still render a friendly label after an asset has been
 * soft-deleted — the assignment record outlives its target.
 */
export async function lookupAsset(client: ApiClient, id: string): Promise<AssetView | null> {
    const trimmed = id.trim();
    if (trimmed === "") return null;

    try {
        return await getAsset(client, trimmed, true);
    } catch (error) {
        if (error instanceof ApiResponseError && error.status === 404) {
            return null;
        }
        throw error;
    }
}

export async function createAsset(
    client: ApiClient,
    payload: AssetUpsertPayload,
): Promise<AssetView> {
    const response = await client.fetch("/api/assets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return await readMutationResponse(response);
}

export async function updateAsset(
    client: ApiClient,
    payload: AssetUpsertPayload,
): Promise<AssetView> {
    const response = await client.fetch(`/api/assets/${encodeURIComponent(payload.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            assetId: payload.id,
            sageOfficeId: payload.sageOfficeId,
            type: payload.type,
            make: payload.make,
            model: payload.model,
            serialNumber: payload.serialNumber,
            assetTag: payload.assetTag,
            purchaseDate: payload.purchaseDate,
            purchasePrice: payload.purchasePrice,
        }),
    });
    return await readMutationResponse(response);
}

export async function deleteAsset(client: ApiClient, id: string): Promise<void> {
    const response = await client.fetch(`/api/assets/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
}

export async function restoreAsset(client: ApiClient, id: string): Promise<AssetView> {
    const response = await client.fetch(`/api/assets/${encodeURIComponent(id)}/restore`, {
        method: "POST",
    });
    return await readMutationResponse(response);
}

async function readMutationResponse(response: Response): Promise<AssetView> {
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return assetMutationResponseSchema.parse(raw).asset;
}

/**
 * Bulk-import call. Hits `POST /api/assets/bulk` with the parsed rows
 * as a JSON array. The server processes each row independently, so a
 * 200 response can include per-row failures — callers should inspect
 * `result.failed` and `result.results` rather than relying on the HTTP
 * status alone.
 *
 * Reconciliation: rows resolve an existing asset by `id` or unique
 * `serialNumber`. Re-uploading the same CSV is safe — already-imported
 * rows come back as `skipped` (same assignment) or `updated` (moved /
 * released) instead of a duplicate or a uniqueness error.
 */
export async function bulkImportAssets(
    client: ApiClient,
    payload: BulkImportAssetsPayload,
): Promise<BulkImportAssetsResult> {
    const response = await client.fetch("/api/assets/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return bulkImportAssetsResponseSchema.parse(raw);
}
